import { Column, Row } from '@/components/container/Container';
import {
	TextBodySmall,
	TextHeadlineMedium,
	TextTitleMedium2,
} from '@/components/text/Texts';
import { cn } from '@/components/ui/cn';
import { PhoneHelper } from '@/common/PhoneHelper';
import { ConciergeLiveMap } from '@/modules/concierge/map/ConciergeLiveMap';
import type { GoogleMapsRoutingResponse } from '@/modules/map/engine/GoogleTypes';
import type {
	GetServicesAndPricesResponse,
	VehicleTypeDto,
} from '@/modules/vehicle/engine/ActivityTypes';
import { Button } from 'primereact/button';
import { useMemo } from 'react';
import { MdArrowBack, MdCheck, MdInfoOutline } from 'react-icons/md';
import {
	firstActiveVehicleType,
	readVehicleTypeIsActive,
} from './NewSendBookingHelpers';
import {
	DeliveryItemType,
	DeliverySize,
	type NewSendBookingFormValues,
} from './formTypes';

const ITEM_TYPE_LABEL: Record<DeliveryItemType, string> = {
	[DeliveryItemType.None]: '—',
	[DeliveryItemType.Document]: 'Document',
	[DeliveryItemType.Food]: 'Food',
	[DeliveryItemType.Clothing]: 'Clothing',
	[DeliveryItemType.Electronic]: 'Electronic',
	[DeliveryItemType.Fragile]: 'Fragile',
	[DeliveryItemType.Medical]: 'Medical',
	[DeliveryItemType.Other]: 'Other',
};

const SIZE_LABEL: Record<DeliverySize, string> = {
	[DeliverySize.None]: '—',
	[DeliverySize.Small]: 'Small (25cm × 25cm)',
	[DeliverySize.Medium]: 'Medium (40cm × 40cm)',
};

const WEIGHT_LABEL: Record<number, string> = {
	1: 'Below 1 kg',
	5: '1–5 kg',
	10: '5–10 kg',
	20: '10–20 kg',
};

function formatPhp(amount: number): string {
	const safe = Number.isFinite(amount) ? amount : 0;
	return new Intl.NumberFormat('en-PH', {
		style: 'currency',
		currency: 'PHP',
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(safe);
}

function readVehicleAmount(vehicle: VehicleTypeDto | undefined): number {
	if (!vehicle) return 0;
	const o = vehicle as unknown as Record<string, unknown>;
	const raw = o.Amount ?? o.amount;
	if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
	if (typeof raw === 'string' && raw.trim() !== '') {
		const parsed = Number(raw);
		return Number.isFinite(parsed) ? parsed : 0;
	}
	return 0;
}

function formatDisplayPhone(
	raw: string,
	prefixCode: string,
	dialCode: string,
): string {
	const trimmed = raw?.trim() || '';
	if (!trimmed) return '—';
	try {
		const prepared = PhoneHelper.preparePhoneNumber(trimmed, prefixCode);
		return prepared;
	} catch {
		return `${dialCode} ${trimmed}`;
	}
}

type Props = {
	values: NewSendBookingFormValues;
	pickupLabel: string;
	dropoffLabel: string;
	googleMapsRoute: GoogleMapsRoutingResponse | null;
	/** Object URL from parent (stable across step changes). */
	parcelPhotoPreviewUrl: string | null;
	senderDialCode: string;
	recipientDialCode: string;
	routePricingData: GetServicesAndPricesResponse | undefined;
	/** When set (from Step 2 vehicle choice), fare uses this row instead of API default. */
	selectedPricingVehicle?: VehicleTypeDto;
	onEditDetails: () => void;
	onConfirmBooking: () => void;
	confirmBookingPending?: boolean;
};

const SummaryField = ({
	label,
	value,
	className,
}: {
	label: string;
	value: string;
	className?: string;
}) => (
	<Column className={cn('min-w-0 gap-0.5', className)}>
		<span className="font-poppins text-label-medium text-primary60">{label}</span>
		<span className="font-poppins text-body-large text-primary break-words">
			{value}
		</span>
	</Column>
);

const NewSendBookingStep3 = ({
	values,
	pickupLabel,
	dropoffLabel,
	googleMapsRoute,
	parcelPhotoPreviewUrl,
	senderDialCode,
	recipientDialCode,
	routePricingData,
	selectedPricingVehicle,
	onEditDetails,
	onConfirmBooking,
	confirmBookingPending = false,
}: Props) => {
	const activeVehicle = useMemo(() => {
		if (selectedPricingVehicle) return selectedPricingVehicle;
		const types = routePricingData?.VehicleTypes;
		if (!types?.length) return undefined;
		const byActive = firstActiveVehicleType(types);
		if (byActive) return byActive;
		const withAmount = types.find((t) => readVehicleAmount(t) > 0);
		if (withAmount) return withAmount;
		return types[0];
	}, [selectedPricingVehicle, routePricingData]);

	const totalFare = readVehicleAmount(activeVehicle);

	const payerIsX4b = values.payer === 'x4b';
	const payerIsSenderCash = values.payer === 'cash_sender';
	const payerIsRecipientCash = values.payer === 'cash_recipient';

	const paymentTitle = payerIsX4b
		? 'Paid via X4B Program'
		: payerIsSenderCash
			? 'Cash Payment by Sender'
			: payerIsRecipientCash
				? 'Cash Payment by Recipient'
				: 'Payment';

	const paymentSubtitle = payerIsX4b
		? 'Company covers the full fare for this booking.'
		: payerIsSenderCash
			? 'The rider will collect cash from the sender.'
			: payerIsRecipientCash
				? 'The rider will collect cash from the recipient.'
				: '';

	const paymentCardClass = payerIsX4b
		? 'border-[#2E7D32]/40 bg-[#E8F5E9]'
		: payerIsSenderCash
			? 'border-[#1890FF] bg-[#E6F4FF]'
			: payerIsRecipientCash
				? 'border-[#FA8C16] bg-[#FFF4E6]'
				: 'border-[#CCDFE5] bg-primary99/40';

	const paymentAccentClass = payerIsX4b
		? 'text-[#2E7D32]'
		: payerIsSenderCash
			? 'text-[#1890FF]'
			: payerIsRecipientCash
				? 'text-[#FA8C16]'
				: 'text-primary60';

	const paymentTitleClass = payerIsX4b
		? 'text-[#1B5E20]'
		: payerIsSenderCash
			? 'text-[#1890FF]'
			: payerIsRecipientCash
				? 'text-[#FA8C16]'
				: 'text-primary';

	const companyPays = payerIsX4b ? totalFare : 0;
	const cashToCollect = payerIsX4b ? 0 : totalFare;
	const collectedFrom = payerIsX4b
		? 'N/A'
		: payerIsSenderCash
			? `${values.senderContactName?.trim() || 'Sender'} (sender)`
			: payerIsRecipientCash
				? `${values.recipientContactName?.trim() || 'Recipient'} (recipient)`
				: '—';

	const pickupAddressText =
		values.pickUpAddress?.Address ||
		values.pickUpAddress?.PlaceName ||
		pickupLabel ||
		'—';
	const dropoffAddressText =
		values.dropOffAddress?.Address ||
		values.dropOffAddress?.PlaceName ||
		dropoffLabel ||
		'—';

	return (
		<Column className="new-send-booking-summary-card w-full max-w-[min(440px,94vw)] shrink-0 rounded-[20px] bg-white px-5 py-6 shadow-[0_8px_32px_rgba(10,65,89,0.12)] sm:px-8">
			<TextHeadlineMedium text="Summary" className="text-primary" />
			<div className="my-4 h-px w-full bg-[#CCDFE5]" />

			{/* 1. Sender & recipient */}
			<Column className="gap-3">
				<TextTitleMedium2
					text="Sender & Recipient Information"
					className="text-primary font-semibold"
				/>
				<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
					<SummaryField
						label="Sender name"
						value={values.senderContactName?.trim() || '—'}
					/>
					<SummaryField
						label="Mobile number"
						value={formatDisplayPhone(
							values.senderPhone,
							values.senderMobileNumberPrefix,
							senderDialCode,
						)}
					/>
					<SummaryField
						label="Recipient name"
						value={values.recipientContactName?.trim() || '—'}
					/>
					<SummaryField
						label="Mobile number"
						value={formatDisplayPhone(
							values.recipientPhone,
							values.recipientMobileNumberPrefix,
							recipientDialCode,
						)}
					/>
				</div>
			</Column>

			<div className="my-5 h-px w-full bg-[#CCDFE5]" />

			{/* 2. Route */}
			<Column className="gap-3">
				<TextTitleMedium2
					text="Route information"
					className="text-primary font-semibold"
				/>
				<Column className="gap-3 rounded-[12px] bg-primary99/50 p-4">
					<div className="grid grid-cols-[auto_minmax(0,1fr)] items-stretch gap-x-3 gap-y-4">
						<div className="row-span-2 flex w-5 shrink-0 justify-center self-stretch py-0.5">
							<img
								src="/images/concierge/pickup-oneway.svg"
								alt=""
								className="h-full min-h-[5rem] w-5 object-contain object-top"
								aria-hidden
							/>
						</div>
						<Column className="min-w-0 gap-0.5">
							<span className="font-poppins text-label-medium text-primary60">
								Pickup address
							</span>
							<span className="font-poppins text-body-large text-primary">
								{pickupAddressText}
							</span>
						</Column>
						<Column className="min-w-0 gap-0.5">
							<span className="font-poppins text-label-medium text-primary60">
								Dropoff address
							</span>
							<span className="font-poppins text-body-large text-primary">
								{dropoffAddressText}
							</span>
						</Column>
					</div>
					<div className="mt-1 h-[180px] w-full overflow-hidden rounded-[10px] border border-[#CCDFE5]">
						<ConciergeLiveMap
							pickupLocationName={pickupLabel}
							dropOffLocationName={dropoffLabel}
							initialRoute={googleMapsRoute}
							activity={undefined}
							mapDivClassName="h-[180px] min-h-[180px] w-full"
							markersAsDots
						/>
					</div>
				</Column>
			</Column>

			<div className="my-5 h-px w-full bg-[#CCDFE5]" />

			{/* 3. Parcel */}
			<Column className="gap-3">
				<TextTitleMedium2
					text="Parcel information"
					className="text-primary font-semibold"
				/>
				<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
					<SummaryField
						label="Item type"
						value={ITEM_TYPE_LABEL[values.deliveryItemType]}
					/>
					<SummaryField
						label="Size"
						value={SIZE_LABEL[values.parcelSize]}
					/>
					<SummaryField
						label="Weight"
						value={
							WEIGHT_LABEL[values.weightMaxKg] ?? `${values.weightMaxKg} kg`
						}
					/>
				</div>
				<SummaryField
					label="Notes"
					value={
						values.driverNotes?.trim()
							? values.driverNotes.trim()
							: '—'
					}
				/>
			</Column>

			<div className="my-5 h-px w-full bg-[#CCDFE5]" />

			{/* 4. Parcel photo */}
			<Column className="gap-3">
				<TextTitleMedium2
					text="Parcel photo"
					className="text-primary font-semibold"
				/>
				<span className="font-poppins text-label-medium text-primary60">
					Pickup photo
				</span>
				{parcelPhotoPreviewUrl ? (
					<img
						src={parcelPhotoPreviewUrl}
						alt=""
						className="max-h-48 w-full rounded-[10px] border border-[#CCDFE5] object-contain bg-white"
					/>
				) : (
					<TextBodySmall className="text-onSurfaceVariant" text="—" />
				)}
			</Column>

			<div className="my-5 h-px w-full bg-[#CCDFE5]" />

			{/* 5. Payment */}
			<Column className="gap-3">
				<TextTitleMedium2
					text="Payment method"
					className="text-primary font-semibold"
				/>
				<Row
					className={cn(
						'items-start gap-2 rounded-[12px] border px-4 py-3',
						paymentCardClass,
					)}
				>
					<MdCheck
						className={cn('mt-0.5 shrink-0', paymentAccentClass)}
						size={22}
					/>
					<Column className="min-w-0 gap-0.5">
						<span
							className={cn(
								'font-poppins text-label-large2 font-semibold',
								paymentTitleClass,
							)}
						>
							{paymentTitle}
						</span>
						{paymentSubtitle ? (
							<span className="font-poppins text-body-small text-onSurface">
								{paymentSubtitle}
							</span>
						) : null}
					</Column>
				</Row>
			</Column>

			<div className="my-5 h-px w-full bg-[#CCDFE5]" />

			{/* 6. Fare */}
			<Column className="gap-3">
			
				<Row className="justify-between gap-4">
					<span className="font-poppins text-title-medium2 font-semibold text-primary">
						Total Fare
					</span>
					<span className="shrink-0 font-poppins text-title-medium2 font-semibold text-[#2E7D32]">
						{formatPhp(totalFare)}
					</span>
				</Row>
			</Column>

			{/* Footer payment summary */}
			<Column className="mt-5 gap-3 rounded-[12px] bg-primary99/50 p-4">
				<Row className="justify-between gap-4">
					<span className="font-poppins text-body-large text-onSurface">
						Company pays
					</span>
					<span className="shrink-0 font-poppins text-body-large font-medium text-primary">
						{formatPhp(companyPays)}
					</span>
				</Row>
				<Row className="justify-between gap-4">
					<span className="font-poppins text-body-large text-onSurface">
						Cash to collect
					</span>
					<span className="shrink-0 font-poppins text-body-large font-medium text-primary">
						{formatPhp(cashToCollect)}
					</span>
				</Row>
				<Row className="justify-between gap-4">
					<span className="font-poppins text-body-large text-onSurface">
						Collected from
					</span>
					<span className="shrink-0 max-w-[55%] text-right font-poppins text-body-large font-medium text-primary">
						{collectedFrom}
					</span>
				</Row>
				{payerIsX4b ? (
					<Row className="mt-1 gap-2">
						<MdInfoOutline
							className="mt-0.5 shrink-0 text-primary60"
							size={18}
						/>
						<TextBodySmall
							className="text-primary"
							text="The rider will not collect cash for this booking."
						/>
					</Row>
				) : null}
			</Column>

			<Row className="mt-6 flex-wrap items-center justify-between gap-3 border-t border-[#CCDFE5] pt-5">
				<Button
					type="button"
					label="Edit details"
					icon={<MdArrowBack className="text-primary" />}
					onClick={onEditDetails}
					pt={{
						root: {
							className:
								'rounded-full border border-primary60 bg-white px-5 py-2.5',
						},
						label: {
							className:
								'font-poppins text-label-large2 text-primary',
						},
					}}
				/>
				<Button
					type="button"
					label={
						confirmBookingPending ? 'Submitting…' : 'Confirm booking'
					}
					loading={confirmBookingPending}
					disabled={confirmBookingPending}
					onClick={() => {
						onConfirmBooking();
					}}
					pt={{
						root: {
							className:
								'rounded-full bg-primary60 px-5 py-2.5 border-primary60',
						},
						label: {
							className:
								'font-poppins text-label-large2 text-white',
						},
					}}
				/>
			</Row>
		</Column>
	);
};

export default NewSendBookingStep3;
